import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  beginLocalVoteSuppression,
  cancelLocalVoteSuppression,
  markLocalVoteSound,
  playVoteSound
} from "../lib/audio";
import { useRealtimeOptionShake } from "../hooks/useRealtimeOptionShake";
import { playVoteConfettiAtPointer } from "../lib/confetti";
import { supportCommunityNameForMarket } from "../lib/supportCommunities";
import {
  formatMarketOptionVotes,
  formatMarketTotalVotes,
  formatMarketVoteAmountOptionLabel,
  marketVoteAmountOptionsForDisplay
} from "../lib/marketTokenDisplay.js";
import { formatUtcDateTime } from "../lib/timeDisplay";
import { getVoteAnimationVars } from "../lib/voteAnimation";
import { useWalletAuth } from "../lib/walletAuth";
import { isTermsPreconditionError } from "../lib/api";
import { useTermsAcceptance } from "../lib/termsAcceptanceContext.jsx";
import { MarketAvatar } from "./MarketAvatar";
import { MarketExternalLinks } from "./MarketExternalLinks";
import { MarketShareButton } from "./MarketShareButton";
import { usePrivyVoteConfirmation } from "../lib/privyVoteConfirmation.js";
import { PrivyVoteConfirmationModal } from "./PrivyVoteConfirmationModal.jsx";
import { genesisMarketBadgeDescription, genesisMarketBadgeLabel, resolveGenesisVoteErrorMessage } from "../lib/genesisMarket";

export function CompactMarketCard({ market }) {
  const [selectedVoteAmountKey, setSelectedVoteAmountKey] = useState("");
  const [busyOption, setBusyOption] = useState("");
  const [voteError, setVoteError] = useState("");
  const [authNoticeShakeTick, setAuthNoticeShakeTick] = useState(0);
  const location = useLocation();
  const { getShakeClass } = useRealtimeOptionShake(market);
  const { isAuthenticated, session, wallet, walletCanSignTransaction } = useWalletAuth();
  const { requireCurrentTerms } = useTermsAcceptance();
  const privyVoteConfirmation = usePrivyVoteConfirmation({ market, wallet });
  const needsWalletReconnect = isAuthenticated && !walletCanSignTransaction;
  const authNoticeShakeClass = authNoticeShakeTick % 2 === 0 ? "vote-shake-a" : "vote-shake-b";
  const affiliateId = session?.walletAddress || session?.userId || "";
  const genesisBadgeLabel = genesisMarketBadgeLabel(market);
  const genesisBadgeDescription = genesisMarketBadgeDescription(market);
  const voteAmountOptions = marketVoteAmountOptionsForDisplay(market);
  const selectedVoteAmount =
    voteAmountOptions.find((option) => option.key === selectedVoteAmountKey) ||
    voteAmountOptions[0] ||
    null;
  const selectedVoteAmountValue = selectedVoteAmount?.voteAmount || "";

  function triggerAuthNoticeShake() {
    setAuthNoticeShakeTick((previous) => previous + 1);
  }

  async function handleVote(optionId, pointerEvent) {
    if (!isAuthenticated || !walletCanSignTransaction) {
      setVoteError("");
      triggerAuthNoticeShake();
      return;
    }

    const confettiPointer = pointerEvent?.nativeEvent || pointerEvent || null;
    try {
      const termsReady = await requireCurrentTerms({ surface: "modal", returnTo: location.pathname });
      if (!termsReady) return;

      setBusyOption(optionId);
      setVoteError("");
      beginLocalVoteSuppression(market.id);
      const { signAndSubmitVote } = await import("../lib/voteTransactions");
      await signAndSubmitVote({
        wallet,
        marketId: market.id,
        optionId,
        voteAmount: selectedVoteAmountValue,
        paymentTokenId: market.paymentToken?.id || market.paymentTokenId || "sol",
        confirmEmbeddedTransaction: privyVoteConfirmation.confirmEmbeddedTransaction
      });
      playVoteConfettiAtPointer(confettiPointer);
      markLocalVoteSound(market.id);
      await playVoteSound();
    } catch (error) {
      cancelLocalVoteSuppression(market.id);
      if (isTermsPreconditionError(error)) {
        await requireCurrentTerms({ surface: "modal", returnTo: location.pathname });
        return;
      }
      if (error?.code === "vote_confirmation_cancelled") {
        return;
      }
      setVoteError(resolveGenesisVoteErrorMessage(error, market));
    } finally {
      setBusyOption("");
    }
  }

  return (
    <article className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <MarketAvatar imageUrl={market.imageUrl} title={market.title} size="sm" />
          <h3 className="text-sm font-semibold text-zinc-100">
            <Link to={`/market/${market.id}`} className="hover:text-blue-300">
              {market.title}
            </Link>
          </h3>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {genesisBadgeLabel ? (
            <span
              className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5 text-center text-[11px] text-cyan-300"
              title={genesisBadgeDescription || genesisBadgeLabel}
            >
              Genesis only
            </span>
          ) : null}
          <span className="rounded-full border border-blue-500/40 px-2 py-0.5 text-center text-[11px] text-blue-400">
            {formatMarketTotalVotes(market)}
          </span>
          <MarketShareButton market={market} affiliateId={affiliateId} className="px-2 py-0.5 text-[11px]" />
        </div>
      </div>

      <p className="mt-1 line-clamp-2 text-xs text-zinc-300">{market.summary}</p>
      <p className="mt-1 text-xs text-zinc-400">This market supports {supportCommunityNameForMarket(market)}.</p>
      <MarketExternalLinks xUrl={market.xUrl} websiteUrl={market.websiteUrl} className="mt-2" />

      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-zinc-400">Ends (UTC) {formatUtcDateTime(market.endAt)}</span>
        <label className="text-[11px] text-zinc-300">
          <span className="mr-1">Vote</span>
          <select
            value={selectedVoteAmount?.key || ""}
            onChange={(event) => {
              if (!isAuthenticated || !walletCanSignTransaction) {
                triggerAuthNoticeShake();
              }
              setSelectedVoteAmountKey(event.target.value);
            }}
            className="theme-select rounded px-1.5 py-0.5"
            disabled={busyOption.length > 0 || voteAmountOptions.length === 0}
          >
            {voteAmountOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {formatMarketVoteAmountOptionLabel(option, market)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-2 grid gap-1">
        {market.options.map((option) => {
          const fillStyle = {
            width: `${option.percentage}%`,
            ...getVoteAnimationVars(market.id, option.id)
          };
          return (
            <button
              key={option.id}
              type="button"
              onClick={(event) => handleVote(option.id, event)}
              disabled={busyOption.length > 0 || !selectedVoteAmountValue}
              className={[
                "vote-option-surface rounded-md border px-2 py-1 text-left text-xs disabled:cursor-not-allowed disabled:opacity-70",
                getShakeClass(option.id)
              ].join(" ")}
            >
              <span
                className="vote-option-fill"
                style={fillStyle}
                aria-hidden
              />
              <span className="relative z-10 flex items-center justify-between gap-2">
                <span>{option.label}</span>
                <span>{formatMarketOptionVotes(option, market)} ({option.percentage}%)</span>
              </span>
            </button>
          );
        })}
      </div>
      {!isAuthenticated ? (
        <p className={["mt-2 text-xs text-amber-300", authNoticeShakeClass].join(" ")}>
          Connect and authenticate your wallet to cast votes.
        </p>
      ) : needsWalletReconnect ? (
        <p className={["mt-2 text-xs text-amber-300", authNoticeShakeClass].join(" ")}>
          Reconnect your wallet from the header before voting.
        </p>
      ) : voteError ? (
        <p className="mt-2 text-xs text-rose-300">{voteError}</p>
      ) : null}
      <PrivyVoteConfirmationModal {...privyVoteConfirmation.modalProps} />
    </article>
  );
}
